import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MagneticButton } from "../MagneticButton";
import { greetings } from "../../data/content";

const VERSE_TEXT = "I can do all things through Christ which strengtheneth me.";
const TYPE_SPEED = 38; // ms per char
const HOLD_AFTER_TYPED = 4200;
const GREETING_INTERVAL = 2600;

const onScrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Typewriter = () => {
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("typing"); // typing | holding | deleting

  useEffect(() => {
    let t;
    if (phase === "typing") {
      if (index < VERSE_TEXT.length) {
        t = setTimeout(() => setIndex((i) => i + 1), TYPE_SPEED);
      } else {
        t = setTimeout(() => setPhase("holding"), 100);
      }
    } else if (phase === "holding") {
      t = setTimeout(() => setPhase("deleting"), HOLD_AFTER_TYPED);
    } else if (phase === "deleting") {
      if (index > 0) {
        t = setTimeout(() => setIndex((i) => i - 1), TYPE_SPEED / 2);
      } else {
        t = setTimeout(() => setPhase("typing"), 600);
      }
    }
    return () => clearTimeout(t);
  }, [index, phase]);

  return (
    <span className="italic">
      <span>{VERSE_TEXT.slice(0, index)}</span>
      <span className="typewriter-caret text-magenta-500">|</span>
    </span>
  );
};

const CyclingGreeting = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((n) => (n + 1) % greetings.length), GREETING_INTERVAL);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="relative inline-block align-baseline" aria-live="polite">
      <AnimatePresence mode="wait">
        <motion.span
          key={greetings[i].word}
          initial={{ y: "40%", opacity: 0, filter: "blur(8px)" }}
          animate={{ y: "0%", opacity: 1, filter: "blur(0px)" }}
          exit={{ y: "-40%", opacity: 0, filter: "blur(8px)" }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="inline-block"
        >
          {greetings[i].word}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

export const Hero = () => {
  return (
    <section id="hero" className="relative overflow-hidden">
      {/* very subtle background */}
      <div className="absolute inset-0 -z-10 bg-grid opacity-30" />
      <div className="absolute inset-x-0 top-0 -z-10 h-[80vh] bg-[radial-gradient(60%_60%_at_50%_0%,rgba(220,38,38,0.10)_0%,rgba(220,38,38,0)_60%)]" />

      <div className="container-page pt-12 sm:pt-16 lg:pt-20 pb-24 sm:pb-28">
        {/* HERO STAGE  -  centered figure + text z-index sandwich */}
        <div className="relative mx-auto w-full isolate" style={{ minHeight: "min(78vh, 760px)" }}>
          {/* Figure (placeholder  -  see notes for production crop spec) */}
          <div className="absolute inset-0 z-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
            <div
              className="hero-figure-mask absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(56vw,560px)] h-[min(66vh,640px)] bg-no-repeat"
              style={{
                backgroundImage: "url('/assets/hero-figure.jpg')",
                backgroundSize: "cover",
                backgroundPosition: "50% 15%",
                filter: "grayscale(1) contrast(1.12) brightness(0.68)",
                opacity: 0.85
              }}
            />
            {/* soft radial vignette so figure blends into background regardless of screen */}
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_45%,transparent_15%,rgba(5,5,6,0.6)_55%,#050506_85%)]" />
            <div className="pointer-events-none absolute inset-y-0 left-0 w-[28%] bg-gradient-to-r from-ink-950 to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-[28%] bg-gradient-to-l from-ink-950 to-transparent" />
            <div className="pointer-events-none absolute inset-x-0 top-0 h-[18%] bg-gradient-to-b from-ink-950 to-transparent" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[24%] bg-gradient-to-t from-ink-950 to-transparent" />
          </div>

          {/* Centered text  -  Hello + verse */}
          <div className="relative z-20 flex flex-col items-center justify-center text-center px-4 pt-[18vh] sm:pt-[20vh] lg:pt-[22vh]">
            <h1 className="font-display font-bold tracking-tight text-white leading-[0.95] text-[20vw] sm:text-[16vw] lg:text-[14vw] xl:text-[200px] 2xl:text-[220px] drop-shadow-[0_4px_30px_rgba(0,0,0,0.85)]" data-testid="hero-hello">
              <CyclingGreeting />
            </h1>

            <div className="mt-10 sm:mt-12">
              <div className="font-mono text-[11px] sm:text-xs tracking-[0.28em] uppercase text-magenta-300">Philippians 4:13</div>
              <p className="mt-3 font-display text-base sm:text-lg lg:text-xl text-zinc-200 max-w-[42rem] mx-auto leading-relaxed">
                <Typewriter />
              </p>
            </div>

            {/* Buttons  -  wrapped to prevent magnetic crossover */}
            <div className="mt-12 flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
              <span data-magnetic-wrap="true">
                <MagneticButton
                  onClick={() => onScrollTo("projects")}
                  data-testid="hero-primary-cta-button"
                  strength={0.18}
                  radius={70}
                  className="rounded-xl bg-magenta-500 text-ink-950 px-6 py-3.5 text-[15px] font-semibold hover:bg-magenta-300 glow-magenta"
                >
                  Select Work
                </MagneticButton>
              </span>
              <span data-magnetic-wrap="true">
                <MagneticButton
                  as="a"
                  href="/resume.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-secondary-cta-button"
                  strength={0.18}
                  radius={70}
                  className="rounded-xl border border-edge-700 px-6 py-3.5 text-[15px] font-medium text-zinc-100 hover:border-magenta-500/60 hover:bg-graphite-900/40"
                >
                  Download Resume
                </MagneticButton>
              </span>
            </div>
          </div>
        </div>

        <motion.button
          onClick={() => onScrollTo("about")}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          className="mt-6 mx-auto flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
          data-testid="hero-scroll-cue"
          aria-label="Scroll to next section"
        >
          <span className="font-mono text-[10px] tracking-[0.22em] uppercase">Scroll</span>
          <ArrowDown className="w-4 h-4 animate-bounce" />
        </motion.button>
      </div>
    </section>
  );
};
